import React, { useEffect, useState } from 'react';
import { Box, Button, Paper, Stack, Typography, Alert, TextField, IconButton, Tooltip, Chip } from '@mui/material';
import { AddCircle, ContentCopy, CallEnd } from '@mui/icons-material';
import { liveKitTempRoomService, attendanceService } from '../services/api';
import LiveKitRoomView from './LiveKitRoomView';

// Employee-facing "temporary meeting room" widget — the project-agnostic fallback for when no
// Virtual Office project room is available (or for a quick side-huddle): time connected still
// counts toward attendance the same way LiveKitPresence's project rooms do (see
// LiveKitEventProcessor.HandleLeaveAsync on the backend, which treats presence in EITHER room
// type as "still in the virtual office"). Unlike LiveKitPresence, this always renders — it's
// exactly the option employees with no project assignment otherwise lack.
//
// Discovery is a shareable code only (no lobby, no invite list — product decision): the creator
// shares the code out-of-band, and anyone else enters it in "Join with code" below.
interface TempMeetingRoomProps {
  /** Called after Create/Join auto-marks attendance (same two-step flow as LiveKitPresence), so
   * the parent Attendance page can refresh its own attendance status/button state. */
  onAttendanceMarked?: () => void;
}

const reasonText = (reason?: string) => {
  switch (reason) {
    case 'AttendanceRequired': return 'Attendance will be marked automatically when you start or join.';
    case 'EmployeeInactive': return 'Temporary meeting rooms are unavailable for this account.';
    case 'LiveKitDisabled': return 'The virtual office is not enabled yet.';
    case 'RoomNotFound': return 'No active room matches that code. Double-check it with whoever shared it.';
    case 'RoomEnded': return 'That room has already ended.';
    default: return 'Could not connect to that room. Please try again.';
  }
};

interface ActiveSession {
  tempRoomId: number;
  roomCode: string;
  title?: string | null;
  token: string;
  serverUrl: string;
  isCreator: boolean;
}

const TempMeetingRoom: React.FC<TempMeetingRoomProps> = ({ onAttendanceMarked }) => {
  const [session, setSession] = useState<ActiveSession | null>(null);
  const [title, setTitle] = useState('');
  const [joinCode, setJoinCode] = useState('');
  const [starting, setStarting] = useState(false);
  const [joining, setJoining] = useState(false);
  const [ending, setEnding] = useState(false);
  const [error, setError] = useState('');

  // Restore state after a page reload: if we already have an open connection to a temp room
  // (per the backend's session table), re-join it to get a fresh token — no separate resume
  // endpoint needed, this just reuses the normal join flow.
  useEffect(() => {
    let cancelled = false;
    liveKitTempRoomService.getMyActive().then(async (active: any) => {
      if (cancelled || !active) return;
      try {
        const fresh = await liveKitTempRoomService.joinByCode(active.roomCode);
        if (!cancelled && fresh.canJoin && fresh.token && fresh.serverUrl) {
          setSession({
            tempRoomId: fresh.tempRoomId, roomCode: fresh.roomCode, title: fresh.title,
            token: fresh.token, serverUrl: fresh.serverUrl, isCreator: fresh.isCreator,
          });
        }
      } catch {
        // Best-effort restore only — a fallback failure here just leaves the "not connected" view.
      }
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Shared by Start and Join — both need the same "mark attendance if that's the only blocker,
  // then retry" two-step flow as LiveKitPresence.handleJoin.
  const attemptAccess = async (call: () => Promise<any>): Promise<any> => {
    let fresh = await call();
    if (!fresh.canJoin && fresh.reason === 'AttendanceRequired') {
      const now = new Date();
      const checkInTime = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}:00`;
      await attendanceService.markAttendance(checkInTime, 'Present');
      onAttendanceMarked?.();
      fresh = await call();
    }
    return fresh;
  };

  const handleStart = async () => {
    setStarting(true);
    setError('');
    try {
      const fresh = await attemptAccess(() => liveKitTempRoomService.create(title.trim() || undefined));
      if (fresh.canJoin && fresh.token && fresh.serverUrl) {
        setSession({
          tempRoomId: fresh.tempRoomId, roomCode: fresh.roomCode, title: fresh.title,
          token: fresh.token, serverUrl: fresh.serverUrl, isCreator: fresh.isCreator,
        });
        setTitle('');
      } else {
        setError(reasonText(fresh.reason));
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Could not create a temporary meeting room. Please try again.');
    } finally {
      setStarting(false);
    }
  };

  const handleJoin = async () => {
    if (!joinCode.trim()) return;
    setJoining(true);
    setError('');
    try {
      const code = joinCode.trim().toUpperCase();
      const fresh = await attemptAccess(() => liveKitTempRoomService.joinByCode(code));
      if (fresh.canJoin && fresh.token && fresh.serverUrl) {
        setSession({
          tempRoomId: fresh.tempRoomId, roomCode: fresh.roomCode, title: fresh.title,
          token: fresh.token, serverUrl: fresh.serverUrl, isCreator: fresh.isCreator,
        });
        setJoinCode('');
      } else {
        setError(reasonText(fresh.reason));
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Could not join that room. Please try again.');
    } finally {
      setJoining(false);
    }
  };

  const handleDisconnected = () => {
    setSession(null);
  };

  const handleEndMeeting = async () => {
    if (!session) return;
    setEnding(true);
    try {
      await liveKitTempRoomService.end(session.tempRoomId);
      setSession(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Could not end the meeting. Please try again.');
    } finally {
      setEnding(false);
    }
  };

  const handleCopyCode = () => {
    if (session) navigator.clipboard?.writeText(session.roomCode).catch(() => {});
  };

  if (session) {
    return (
      <Paper sx={{ mb: 3, overflow: 'hidden' }}>
        <Box sx={{ px: 2, py: 1, bgcolor: 'grey.900', color: 'white' }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="body2">
                Temporary Meeting{session.title ? ` — ${session.title}` : ''}
              </Typography>
              <Chip label={`Code: ${session.roomCode}`} size="small" sx={{ bgcolor: 'grey.700', color: 'white' }} />
              <Tooltip title="Copy code">
                <IconButton size="small" onClick={handleCopyCode} sx={{ color: 'white' }}>
                  <ContentCopy fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
            {session.isCreator && (
              <Button
                size="small"
                variant="outlined"
                color="error"
                startIcon={<CallEnd fontSize="small" />}
                onClick={handleEndMeeting}
                disabled={ending}
                sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.4)' }}
              >
                {ending ? 'Ending…' : 'End Meeting'}
              </Button>
            )}
          </Stack>
        </Box>
        <LiveKitRoomView
          token={session.token}
          serverUrl={session.serverUrl}
          onDisconnected={handleDisconnected}
        />
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 2, mb: 3 }}>
      <Typography variant="subtitle1" fontWeight={600} gutterBottom>Temporary Meeting Room</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        No project room available, or just need a quick side call? Start one here — time connected
        still counts toward your attendance.
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'flex-start' }}>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <TextField
            size="small"
            label="Title (optional)"
            value={title}
            onChange={e => setTitle(e.target.value)}
            sx={{ minWidth: 180 }}
          />
          <Button
            variant="contained"
            color="success"
            startIcon={<AddCircle />}
            onClick={handleStart}
            disabled={starting}
          >
            {starting ? 'Starting…' : 'Start Temporary Meeting'}
          </Button>
        </Stack>
        <Stack direction="row" spacing={1}>
          <TextField
            size="small"
            label="Room code"
            value={joinCode}
            onChange={e => setJoinCode(e.target.value.toUpperCase())}
            inputProps={{ maxLength: 12, style: { textTransform: 'uppercase' } }}
            sx={{ width: 140 }}
          />
          <Button
            variant="outlined"
            onClick={handleJoin}
            disabled={joining || !joinCode.trim()}
          >
            {joining ? 'Joining…' : 'Join with code'}
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );
};

export default TempMeetingRoom;
